import CheckmarkIcon from '../../assets/icons/CheckmarkIcon';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import useTheme from '../../hooks/ui/useTheme';
import classnames from 'classnames';
import { useMemo } from 'react';

interface Props {
  placeholder: string;
  value: string;
  id?: string;
  type?: 'text' | 'number' | 'email' | 'uppercase';
  isComplete?: boolean;
  validationErrors?: string[];
  disabled?: boolean;
  onChange: (value: string) => void;
  classes?: {
    container?: string;
    inputWrapper?: string;
    inputClassName?: string;
  };
}

export default function CheckmarkTextInput(props: Props) {
  const { id, type, value, placeholder, isComplete, validationErrors, disabled, classes, onChange } = props;

  const theme = useTheme();
  const isValid = !validationErrors || validationErrors.length === 0;

  const actionIcon = useMemo(() => {
    if (!value || value?.length === 0 || disabled) {
      return null;
    }

    if (isComplete) {
      return (
        <div className={checkmarkIconContainer}>
          <CheckmarkIcon className={iconStyle} color={theme.color.white['100']} />
        </div>
      );
    }

    return (
      <div className={removeIconContainer} onClick={() => onChange('')}>
        <CloseIcon className={iconStyle} color={theme.color.gray['500']} />
      </div>
    );
  }, [value, disabled]);

  function changeValue(e: any) {
    switch (type) {
      case 'number':
        return /^\d+$/.test(e.target.value) || e.target.value === '' ? onChange && onChange(e.target.value) : undefined;
      case 'uppercase':
        return onChange && onChange(e.target.value.toUpperCase());
      default:
        return onChange && onChange(e.target.value);
    }
  }

  function getType(): string {
    switch (type) {
      case 'email':
        return 'email';
      default:
        return 'text';
    }
  }

  return (
    <div className={classes?.container}>
      <div
        className={classnames(
          inputWrapper,
          { 'border-danger': !isValid, 'bg-gray dark:bg-black-600': disabled },
          classes?.inputWrapper,
        )}
      >
        <input
          id={id}
          type={getType()}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={changeValue}
          className="w-full placeholder:text-muted-foreground disabled:pointer-events-none disabled:text-muted-foreground"
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
        />
        {Boolean(actionIcon) && <div className="ml-2">{actionIcon}</div>}
      </div>
      {validationErrors && (
        <div>
          {validationErrors.map((item) => (
            <p className="mt-xs text-danger text-sm" key={`validation-error-${item}`}>
              {item}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const inputWrapper =
  'bg-white text-brand-text-primary flex flex-row p-sm items-center w-full border rounded-md h-[36px]';

const removeIconContainer = 'bg-gray-300 rounded-full flex justify-center items-center w-5 h-5 cursor-pointer';

const checkmarkIconContainer = 'bg-primary rounded-full flex justify-center items-center w-5 h-5';

const iconStyle = 'h-2 w-2';
